import type { PDFFont, PDFPage } from 'pdf-lib';

/**
 * Sign-in slips: one per candidate, ten to an A4 sheet, cut along the dashes.
 *
 * Made in the teacher's own browser from passwords the server has just handed
 * back. A password is stored only as a hash, so this is the one moment it can
 * be printed — and doing it here means it is never written down anywhere
 * else: not on the server, not in a file the server keeps.
 *
 * Letters and numbers only, in one plain Latin face. A character the face
 * cannot draw prints as "?" rather than breaking the sheet.
 */

export interface Slip {
  /** The name or code the centre enrolled them under. */
  name: string;
  candidateNumber: string | null;
  /** What they type in the sign-in box, as it should be printed. */
  signIn: string;
  password: string;
}

export interface SlipSheet {
  centre: string;
  /** Where to sign in, without the scheme: "targets.uz/o/riverside". */
  address: string;
  slips: Slip[];
}

export interface SlipFonts {
  regular: Uint8Array;
  bold: Uint8Array;
  mono: Uint8Array;
}

// A4 in points, and the grid of slips on it.
const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN = 28;
const COLS = 2;
const ROWS = 5;
export const SLIPS_PER_PAGE = COLS * ROWS;
const SLIP_W = (PAGE_W - MARGIN * 2) / COLS;
const SLIP_H = (PAGE_H - MARGIN * 2) / ROWS;
const PAD = 16;

/** Only what the face can draw; anything else becomes "?". */
function drawable(text: string, font: PDFFont): string {
  const known = new Set(font.getCharacterSet());
  return [...text].map((ch) => (known.has(ch.codePointAt(0)!) ? ch : '?')).join('');
}

/** Shortened with an ellipsis to fit a width, so a long name never runs off the slip. */
function fit(text: string, font: PDFFont, size: number, width: number): string {
  if (font.widthOfTextAtSize(text, size) <= width) return text;
  let out = text;
  while (out.length > 1 && font.widthOfTextAtSize(`${out}...`, size) > width) out = out.slice(0, -1);
  return `${out}...`;
}

export async function buildSlipsPdf(sheet: SlipSheet, fonts: SlipFonts): Promise<Uint8Array> {
  const { PDFDocument, rgb } = await import('pdf-lib');
  const fontkit = (await import('@pdf-lib/fontkit')).default;

  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  doc.setTitle(`Sign-in slips — ${sheet.centre}`);
  doc.setCreator('Target');
  const regular = await doc.embedFont(fonts.regular, { subset: true });
  const bold = await doc.embedFont(fonts.bold, { subset: true });
  const mono = await doc.embedFont(fonts.mono, { subset: true });

  const ink = rgb(0.09, 0.08, 0.06);
  const muted = rgb(0.45, 0.43, 0.38);
  const rule = rgb(0.72, 0.7, 0.66);
  const inner = SLIP_W - PAD * 2;

  const text = (page: PDFPage, value: string, x: number, y: number, size: number, font: PDFFont, color = ink) =>
    page.drawText(fit(drawable(value, font), font, size, inner), { x, y, size, font, color });

  let page: PDFPage | null = null;
  sheet.slips.forEach((slip, i) => {
    const onPage = i % SLIPS_PER_PAGE;
    if (onPage === 0) {
      page = doc.addPage([PAGE_W, PAGE_H]);
      // Cut lines: dashed, light, between and around every slip.
      for (let c = 0; c <= COLS; c += 1) {
        const x = MARGIN + c * SLIP_W;
        page.drawLine({ start: { x, y: MARGIN }, end: { x, y: PAGE_H - MARGIN }, thickness: 0.5, color: rule, dashArray: [3, 3] });
      }
      for (let r = 0; r <= ROWS; r += 1) {
        const y = MARGIN + r * SLIP_H;
        page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 0.5, color: rule, dashArray: [3, 3] });
      }
    }
    const p = page!;
    const col = onPage % COLS;
    const row = Math.floor(onPage / COLS);
    const left = MARGIN + col * SLIP_W + PAD;
    const top = PAGE_H - MARGIN - row * SLIP_H - PAD;

    // Laid out to finish well clear of the cut line below, so scissors never
    // take the last line.
    text(p, sheet.centre, left, top - 8, 10, bold);
    text(p, slip.name, left, top - 26, 13, bold);
    if (slip.candidateNumber) text(p, `Candidate ${slip.candidateNumber}`, left, top - 39, 8.5, regular, muted);

    text(p, 'SIGN-IN NUMBER', left, top - 56, 7, regular, muted);
    text(p, slip.signIn, left, top - 71, 15, mono);
    text(p, 'PASSWORD', left, top - 87, 7, regular, muted);
    text(p, slip.password, left, top - 102, 15, mono);

    text(p, `Sign in at ${sheet.address}`, left, top - 117, 7.5, regular, muted);
    text(p, 'You choose your own password when you first sign in. Keep this slip private.', left, top - 127, 6.5, regular, muted);
  });

  if (sheet.slips.length === 0) doc.addPage([PAGE_W, PAGE_H]);
  return doc.save();
}

/**
 * The same sheet, built from the app's own copies of the faces and saved
 * straight to the teacher's Downloads. Nothing is sent anywhere.
 */
export async function downloadSlips(sheet: SlipSheet, filename: string): Promise<void> {
  /*
   * Plain TrueType, not the WOFF files the pages use: pdf-lib's font reader
   * mishandles WOFF ("Trying to access beyond buffer length" on ordinary
   * letters such as "h" and on "+"). These are the same IBM Plex faces,
   * unpacked by scripts/woff-to-ttf.mjs; the licence sits beside them.
   */
  const [regularUrl, boldUrl, monoUrl] = await Promise.all([
    import('../assets/slip-fonts/ibm-plex-sans-400.ttf?url'),
    import('../assets/slip-fonts/ibm-plex-sans-600.ttf?url'),
    import('../assets/slip-fonts/ibm-plex-mono-500.ttf?url'),
  ]);
  const bytes = async (url: string) => new Uint8Array(await (await fetch(url)).arrayBuffer());
  const pdf = await buildSlipsPdf(sheet, {
    regular: await bytes(regularUrl.default),
    bold: await bytes(boldUrl.default),
    mono: await bytes(monoUrl.default),
  });
  const blob = new Blob([new Uint8Array(pdf)], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
}

/** Where a candidate of this centre signs in, as printed on the slip. */
export function signInAddress(slug: string | null | undefined): string {
  const host = typeof window !== 'undefined' ? window.location.host : 'targets.uz';
  return slug ? `${host}/o/${slug}` : `${host}/login`;
}
